const CLOUD_CONVERT_EXTS = ['ai', 'eps', 'pdf', 'svg'];
const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'webp'];
const VECTOR_MAX_SIZE = 2048;
const FALLBACK_PIXEL_DENSITY = 600;

const jobTag = `customizer_${typeof window !== 'undefined' ? window.location?.hostname ?? 'localhost' : 'localhost'}`;
const createJobUrl = 'https://sync.api.cloudconvert.com/v2/jobs';

/**
 * Create image element from blob.
 * @param blob - The image blob to create the element from.
 * @returns A promise that resolves to the created image element.
 */
export const createFromBlob = (blob: Blob): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.src = url;
  });
};

/**
 * Convert vector images (ai, eps, pdf, svg) to PNG via CloudConvert.
 * @param file - Original File.
 * @param apiKey - CloudConvert API key.
 * @returns A promise that resolves to the PNG image URL.
 */
export const vectorToPNG = (file: File, apiKey: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      async () => {
        const base64Data = (reader.result as string).split('base64,').pop();
        const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
        const isVector = ['eps', 'svg'].includes(ext);

        const convertBodyParams: Record<string, unknown> = {
          operation: 'convert',
          input_format: ext,
          output_format: 'png',
          input: ['import'],
          alpha: true,
          text_to_path: true,
        };

        if (isVector) {
          const size = await getVectorSize(file, ext);
          if (size) {
            const scaledSize = scaleToMax(size, VECTOR_MAX_SIZE);
            convertBodyParams.width = scaledSize.width;
            convertBodyParams.height = scaledSize.height;
          } else {
            convertBodyParams.pixel_density = FALLBACK_PIXEL_DENSITY;
          }
        }

        const body = {
          tasks: {
            import: {
              operation: 'import/base64',
              file: base64Data,
              filename: file.name,
            },
            conversion_task: { ...convertBodyParams },
            export: {
              operation: 'export/url',
              input: ['conversion_task'],
              inline: true,
              archive_multiple_files: false,
            },
          },
          tag: jobTag,
        };

        try {
          const response = await fetch(createJobUrl, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${apiKey}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
          });

          const { status } = response;

          if (status < 200 || status >= 400) {
            throw new Error(status.toString());
          }

          const job = (await response.json()).data;
          const exportTask = job.tasks.find((task: { name: string }) => task.name === 'export');
          const imageUrl = exportTask.result.files[0].url;
          resolve(imageUrl);
        } catch (err) {
          console.error(err);
          reject(err);
        }
      },
      false,
    );
    reader.readAsDataURL(file);
  });
};

async function getVectorSize(file: File, ext: string): Promise<{ width: number; height: number } | null> {
  switch (ext) {
    case 'svg':
      return await getSvgSize(file);
    case 'eps':
      return await getEpsSize(file);
    default:
      return null;
  }
}

async function getEpsSize(file: File): Promise<{ width: number; height: number } | null> {
  const text = await file.text();
  const match = text.match(
    /%%BoundingBox:\s*([\d.-]+)\s+([\d.-]+)\s+([\d.-]+)\s+([\d.-]+)/,
  );
  if (match) {
    const llx = parseFloat(match[1]);
    const lly = parseFloat(match[2]);
    const urx = parseFloat(match[3]);
    const ury = parseFloat(match[4]);
    return { width: urx - llx, height: ury - lly };
  }
  return null;
}

async function getSvgSize(file: File): Promise<{ width: number; height: number } | null> {
  const text = await file.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'image/svg+xml');
  const svg = doc.documentElement;

  let width = svg.getAttribute('width');
  let height = svg.getAttribute('height');

  if ((!width || !height) && svg.hasAttribute('viewBox')) {
    const viewBox = svg.getAttribute('viewBox')!;
    const [, , vbWidth, vbHeight] = viewBox.split(/\s+/).map(Number);
    width = vbWidth.toString();
    height = vbHeight.toString();
  }

  if (width && height) {
    return { width: parseFloat(width), height: parseFloat(height) };
  }

  return null;
}

function scaleToMax(size: { width: number; height: number }, maxSide: number) {
  const { width, height } = size;
  const scale = maxSide / Math.max(width, height);
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
}

const getImageSizeFromUrl = (url: string): Promise<{ x: number; y: number }> =>
  new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      resolve({ x: img.naturalWidth, y: img.naturalHeight });
    };
    img.onerror = () => {
      resolve({ x: 100, y: 100 });
    };
    img.src = url;
  });

export type FileToImageResult = {
  imageSrc: string;
  imageSize: { x: number; y: number };
  revokeUrl?: () => void;
};

/**
 * Check if file extension requires CloudConvert for conversion.
 */
export const needsCloudConvert = (fileName: string): boolean => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  return ext ? CLOUD_CONVERT_EXTS.includes(ext) : false;
};

/**
 * Convert a file to image URL and size. Handles both raster images and vector formats.
 * - IMAGE_EXTS (jpg, jpeg, png, webp): Uses createFromBlob, returns object URL.
 * - CLOUD_CONVERT_EXTS (ai, eps, pdf, svg): Uses CloudConvert to convert to PNG.
 */
export const fileToImageUrl = async (
  file: File,
  cloudConvertApiKey: string,
): Promise<FileToImageResult> => {
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (!ext) {
    throw new Error('File has no extension');
  }

  if (IMAGE_EXTS.includes(ext)) {
    const img = await createFromBlob(file);
    const imageSize = { x: img.naturalWidth, y: img.naturalHeight };
    const imageSrc = URL.createObjectURL(file);
    return {
      imageSrc,
      imageSize,
      revokeUrl: () => URL.revokeObjectURL(imageSrc),
    };
  }

  if (CLOUD_CONVERT_EXTS.includes(ext)) {
    if (!cloudConvertApiKey) {
      throw new Error(
        'CLOUD_CONVERT_API_KEY is required for vector files (ai, eps, pdf, svg). Add it to your .env file.',
      );
    }
    const imageSrc = await vectorToPNG(file, cloudConvertApiKey);
    const imageSize = await getImageSizeFromUrl(imageSrc);
    return { imageSrc, imageSize };
  }

  throw new Error(`Unsupported file extension: ${ext}`);
};

export const CLOUD_CONVERT_EXTS_LIST = CLOUD_CONVERT_EXTS;
export const IMAGE_EXTS_LIST = IMAGE_EXTS;
